import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import {
    Badge, Button, FormDrawer, FormSection, IconButton, Input, PageHeader, Select, SettingCard,
    Spin, Switch, Textarea, useAlert,
} from '../ui';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import {
    ArrowDown, ArrowUp, Ear, Globe2, Info,
    Languages, ListMusic, Mic2, Pencil, Plus, Save, Trash2,
} from 'lucide-react';
import User from '../../Models/User';
import datas from "../../Assets/data.json";
import env from 'react-dotenv';
import { Crypter } from '../../Functions/Security/Crypter';

/**
 * Paramétrages > Annonces vocales (droit D18).
 * API : POST /api/Annonces/Admin — corps { Option, ... } chiffré AES.
 * Trois onglets : Langues (admins), Textes des annonces, Prononciation des postes.
 */

/** POST api/Annonces/Admin — retourne Value déchiffré. */
function postAdmin(authHeader: () => string, payload: any): Promise<any> {
    return axios.request({
        method: 'post',
        maxBodyLength: Infinity,
        url: env.API_BASE_URL + datas.API_LINKs.Annonces.Admin,
        headers: { 'Content-Type': 'application/json', 'Authorization': authHeader() },
        data: Crypter.encrypt(JSON.stringify(payload)),
    }).then((r: any) => JSON.parse(Crypter.decrypt(r.data)).Value);
}

type Niveau = 'entite' | 'agence' | 'service'

export default function GestionAnnonces() {
    const authHeader = useAuthHeader()
    const auth = useAuthUser()
    const user: User = JSON.parse(JSON.stringify(auth() || {}))
    const alert = useAlert()

    const isAdmin = ['SUADMIN', 'ADMIN', 'IT'].includes(user.RoleId)
    const habilitations: any[] = (() => {
        try { return JSON.parse(Crypter.decrypt(localStorage.getItem(datas.Habilitations) || '')) || [] } catch { return [] }
    })()
    const canEdit = isAdmin || habilitations.some((h: any) => h.CodeHabi === 'D18')

    const [tab, setTab] = useState<'textes' | 'postes' | 'langues'>('textes')
    const [langues, setLangues] = useState<any[]>([])
    const [filiales, setFiliales] = useState<any[]>([])
    const [services, setServices] = useState<any[]>([])

    const languesActives = useMemo(() => langues.filter((l: any) => l.IsActif), [langues])

    useEffect(() => {
        LoadLangues()
        LoadFiliales()
        LoadServices()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    function LoadLangues() {
        postAdmin(authHeader, { Option: 'LANGUES' })
            .then((res: any) => { if (res.StatutCode == 200) setLangues(res.Result || []) })
            .catch((e) => console.log(e))
    }

    function LoadFiliales() {
        axios.get(env.API_BASE_URL + datas.API_LINKs.GetAllFiliales + Crypter.encrypt(user.CodeAbonne),
            { headers: { 'Accept': 'text/plain', 'Authorization': authHeader() } })
            .then((r: any) => {
                const res = JSON.parse(Crypter.decrypt(r.data)).Value
                if (res.StatutCode == 200) setFiliales(res.Result || [])
            })
            .catch((e) => console.log(e))
    }

    function LoadServices() {
        axios.get(env.API_BASE_URL + datas.API_LINKs.GetFilialeServices + Crypter.encrypt(user.CodeAbonne),
            { headers: { 'Accept': 'text/plain', 'Authorization': authHeader() } })
            .then((r: any) => {
                const res = JSON.parse(Crypter.decrypt(r.data)).Value
                if (res.StatutCode == 200) {
                    const svc = Array.isArray(res.Result?.[1]) ? res.Result[1] : (res.Result || [])
                    setServices(svc)
                }
            })
            .catch((e) => console.log(e))
    }

    const tabs = [
        { key: 'textes' as const, label: 'Textes des annonces', icon: <ListMusic size={15} /> },
        { key: 'postes' as const, label: 'Prononciation des postes', icon: <Mic2 size={15} /> },
        ...(isAdmin ? [{ key: 'langues' as const, label: 'Langues', icon: <Languages size={15} /> }] : []),
    ]

    return (
        <div>
            <PageHeader
                title="Annonces vocales"
                subtitle="Langues, textes des annonces et prononciation des postes (droit D18)"
            />

            <div className="flex items-start gap-2 mb-4 px-3 py-2.5 rounded-lg border border-[var(--eb-border)] bg-[var(--eb-primary-soft)]/40 text-sm text-[var(--eb-text)]">
                <Info size={16} className="mt-0.5 flex-shrink-0 text-[var(--eb-primary)]" />
                <span>
                    La voix lit le niveau le plus précis configuré (service &gt; agence &gt; entité).
                    Repères : <b>{'{ticket}'}</b> obligatoire, <b>{'{poste}'}</b> la destination, <b>{'{guichet}'}</b> le numéro de box — <b>{'{service}'}</b> est refusé.
                    Une langue n'est lue que sur les écrans qui ont une voix de cette langue.
                    {!canEdit && <b className="block mt-1 text-amber-600">Lecture seule — le droit D18 « Annonces vocales » est requis pour modifier.</b>}
                </span>
            </div>

            <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-[var(--eb-surface)] border border-[var(--eb-border)] mb-4">
                {tabs.map(t => (
                    <button key={t.key} type="button" onClick={() => setTab(t.key)}
                        className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors
                            ${tab === t.key ? 'bg-[var(--eb-primary)] text-white shadow-sm' : 'text-[var(--eb-text-muted)] hover:text-[var(--eb-text)]'}`}>
                        {t.icon}{t.label}
                    </button>
                ))}
            </div>

            {tab === 'langues' && isAdmin && (
                <TabLangues authHeader={authHeader} langues={langues} onRefresh={LoadLangues} />
            )}
            {tab === 'textes' && (
                <TabTextes authHeader={authHeader} user={user} isAdmin={isAdmin} canEdit={canEdit}
                    languesActives={languesActives} filiales={filiales} services={services} />
            )}
            {tab === 'postes' && (
                <TabPostes authHeader={authHeader} user={user} canEdit={canEdit}
                    languesActives={languesActives} filiales={filiales} services={services} />
            )}
        </div>
    );
}

/* ================================================================== */
/*  Onglet Langues (administrateurs)                                   */
/* ================================================================== */
function TabLangues(props: { authHeader: () => string; langues: any[]; onRefresh: () => void }) {
    const alert = useAlert()
    const [saving, setSaving] = useState(false)
    const [edit, setEdit] = useState<any>(null)   // {} = ajout ; objet langue = édition

    function save() {
        if (!edit?.CodeLangue || !/^[a-z]{2}-[A-Z]{2}$/.test(edit.CodeLangue)) {
            alert.warning('Code langue invalide : format langue-PAYS attendu (ex. fr-FR, en-GB).')
            return
        }
        if (!edit?.Libelle) { alert.warning('Libellé requis (50 caractères max).'); return }
        setSaving(true)
        postAdmin(props.authHeader, { Option: 'LANGUE_ENREGISTRER', Langue: edit })
            .then((res: any) => {
                if (res.StatutCode == 200) { alert.success(res.Message); setEdit(null); props.onRefresh() }
                else alert.warning(res.Message)
            })
            .catch((e) => alert.error('Error', e))
            .finally(() => setSaving(false))
    }

    function supprimer(l: any) {
        setSaving(true)
        postAdmin(props.authHeader, { Option: 'LANGUE_SUPPRIMER', Langue: { CodeLangue: l.CodeLangue } })
            .then((res: any) => {
                if (res.StatutCode == 200) { alert.success(res.Message); props.onRefresh() }
                else alert.warning(res.Message)
            })
            .catch((e) => alert.error('Error', e))
            .finally(() => setSaving(false))
    }

    function desactiver(l: any) {
        setSaving(true)
        postAdmin(props.authHeader, {
            Option: 'LANGUE_ENREGISTRER',
            Langue: { CodeLangue: l.CodeLangue, Libelle: l.Libelle, IsActif: false },
        })
            .then((res: any) => {
                if (res.StatutCode == 200) { alert.success(res.Message); props.onRefresh() }
                else alert.warning(res.Message)
            })
            .catch((e) => alert.error('Error', e))
            .finally(() => setSaving(false))
    }

    return (
        <SettingCard title="Langues de l'entité" icon={<Languages size={18} />}>
            <Spin spinning={saving}>
                <div className="flex justify-end mb-3">
                    <Button variant="secondary" size="sm" startIcon={<Plus size={16} />}
                        onClick={() => setEdit({ CodeLangue: '', Libelle: '', RepliPoste: '', IsActif: true })}>
                        Ajouter une langue
                    </Button>
                </div>
                <div className="overflow-x-auto rounded-lg border border-[var(--eb-border)]">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-[var(--eb-bg)] text-left text-xs text-[var(--eb-text-muted)]">
                                <th className="px-3 py-2 font-semibold">Code</th>
                                <th className="px-3 py-2 font-semibold">Libellé</th>
                                <th className="px-3 py-2 font-semibold">Phrase de repli</th>
                                <th className="px-3 py-2 font-semibold w-16">Ordre</th>
                                <th className="px-3 py-2 font-semibold w-20">Actif</th>
                                <th className="px-3 py-2 font-semibold w-24">Utilisée</th>
                                <th className="px-3 py-2 font-semibold w-28">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {props.langues.map((l: any) => {
                                const utilisee = (l.NbAnnonces || 0) + (l.NbPrononciations || 0) > 0
                                return (
                                    <tr key={l.CodeLangue} className="border-t border-[var(--eb-border)]">
                                        <td className="px-3 py-2 font-mono">{l.CodeLangue}</td>
                                        <td className="px-3 py-2">{l.Libelle}</td>
                                        <td className="px-3 py-2 text-xs text-[var(--eb-text-muted)]">{l.RepliPoste || '—'}</td>
                                        <td className="px-3 py-2">{l.Ordre}</td>
                                        <td className="px-3 py-2">
                                            <Badge color={l.IsActif ? 'green' : 'gray'}>{l.IsActif ? 'Oui' : 'Non'}</Badge>
                                        </td>
                                        <td className="px-3 py-2">
                                            <Badge color={utilisee ? 'blue' : 'gray'}>{utilisee ? 'Oui' : 'Non'}</Badge>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-1">
                                                <IconButton tooltip="Modifier" color="primary" size={28}
                                                    onClick={() => setEdit({ ...l })}>
                                                    <Pencil size={14} />
                                                </IconButton>
                                                {utilisee ? (
                                                    l.IsActif && (
                                                        <IconButton tooltip="Désactiver" color="warning" size={28}
                                                            onClick={() => desactiver(l)}>
                                                            <Globe2 size={14} />
                                                        </IconButton>
                                                    )
                                                ) : (
                                                    <IconButton tooltip="Supprimer" color="danger" size={28}
                                                        onClick={() => supprimer(l)}>
                                                        <Trash2 size={14} />
                                                    </IconButton>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </Spin>

            <FormDrawer open={!!edit} onClose={() => setEdit(null)}
                title={edit?.CodeLangue && props.langues.some((l: any) => l.CodeLangue === edit.CodeLangue) ? `Modifier ${edit.CodeLangue}` : 'Ajouter une langue'}
                hint="Format du code : langue-PAYS (ex. fr-FR, pt-PT). La langue ne sera lue que sur les écrans qui ont une voix de cette langue."
                footer={
                    <>
                        <Button variant="outline" onClick={() => setEdit(null)}>Annuler</Button>
                        <Button variant="secondary" loading={saving} startIcon={<Save size={16} />} onClick={save}>Enregistrer</Button>
                    </>
                }>
                {edit && (
                    <FormSection title="Langue">
                        <Input label="Code (langue-PAYS) *" value={edit.CodeLangue}
                            onChange={(e) => setEdit({ ...edit, CodeLangue: e.target.value })} placeholder="pt-PT" />
                        <Input label="Libellé *" value={edit.Libelle}
                            onChange={(e) => setEdit({ ...edit, Libelle: e.target.value })} placeholder="Portugais" />
                        <Input label="Phrase de repli (facultative — seul repère {guichet})" value={edit.RepliPoste || ''}
                            onChange={(e) => setEdit({ ...edit, RepliPoste: e.target.value })} placeholder="ao balcão {guichet}" />
                        <Input label="Ordre" type="number" value={edit.Ordre ?? ''}
                            onChange={(e) => setEdit({ ...edit, Ordre: Number(e.target.value) || undefined })} />
                        <div className="flex items-center gap-2">
                            <Switch checked={edit.IsActif !== false} onChange={(v) => setEdit({ ...edit, IsActif: v })} />
                            <span className="text-sm">Active</span>
                        </div>
                    </FormSection>
                )}
            </FormDrawer>
        </SettingCard>
    )
}

/* ================================================================== */
/*  Onglet Textes des annonces                                         */
/* ================================================================== */
function TabTextes(props: {
    authHeader: () => string; user: User; isAdmin: boolean; canEdit: boolean;
    languesActives: any[]; filiales: any[]; services: any[];
}) {
    const alert = useAlert()
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [niveau, setNiveau] = useState<Niveau>(props.isAdmin ? 'entite' : 'agence')
    const [codeFiliale, setCodeFiliale] = useState('')
    const [codeS, setCodeS] = useState('')
    const [toutes, setToutes] = useState<any[]>([])       // lignes LIST du périmètre
    const [lignes, setLignes] = useState<any[]>([])       // lignes éditées du niveau choisi
    const [apercu, setApercu] = useState<any[] | null>(null)

    const servicesAgence = props.services.filter((s: any) => !codeFiliale || s.CodeFiliale == codeFiliale)

    useEffect(() => {
        if (!props.isAdmin && !codeFiliale) {
            // Non-admin : limité à son agence
            setCodeFiliale(props.user.CodeFiliale || '')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => { loadList() /* eslint-disable-line react-hooks/exhaustive-deps */ }, [niveau, codeFiliale, codeS])

    function scope() {
        if (niveau === 'service' && codeS) return { CodeS: codeS }
        if (niveau !== 'entite' && codeFiliale) return { CodeFiliale: codeFiliale }
        return {}
    }

    function matchNiveau(r: any): boolean {
        if (niveau === 'entite') return r.Niveau === 'ENTITE'
        if (niveau === 'agence') return r.CodeFiliale === codeFiliale && !r.CodeS
        return r.CodeS === codeS
    }

    function loadList() {
        if (niveau === 'agence' && !codeFiliale) { setToutes([]); setLignes([]); return }
        if (niveau === 'service' && !codeS) { setToutes([]); setLignes([]); return }
        setLoading(true)
        postAdmin(props.authHeader, { Option: 'LIST', ...scope() })
            .then((res: any) => {
                if (res.StatutCode == 200) {
                    const rs = res.Result || []
                    setToutes(rs)
                    setLignes(rs.filter(matchNiveau).map((r: any) => ({
                        CodeLangue: r.CodeLangue, Modele: r.Modele,
                        RepliPoste: r.RepliPoste || '', Priorite: r.Priorite, IsActif: r.IsActif !== false,
                    })))
                } else alert.warning(res.Message)
            })
            .catch((e) => alert.error('Error', e))
            .finally(() => setLoading(false))
    }

    // Niveau réellement appliqué : le plus précis qui a des lignes
    const niveauEffectif = useMemo(() => {
        if (lignes.length) return niveau === 'entite' ? 'Entité' : niveau === 'agence' ? 'Cette agence' : 'Ce service'
        if (niveau === 'service' && codeS) {
            const svc = props.services.find((s: any) => s.CodeS === codeS)
            if (toutes.some((r: any) => r.CodeFiliale === svc?.CodeFiliale && !r.CodeS)) return 'Son agence'
            if (toutes.some((r: any) => r.Niveau === 'ENTITE')) return 'Entité'
            return 'Anciens textes MFR / MEN'
        }
        if (niveau === 'agence' && codeFiliale) {
            if (toutes.some((r: any) => r.Niveau === 'ENTITE')) return 'Entité'
            return 'Anciens textes MFR / MEN'
        }
        return 'Anciens textes MFR / MEN'
    }, [lignes, toutes, niveau, codeS, codeFiliale, props.services])

    function personnaliser() {
        // Reprend les lignes du niveau au-dessus comme gabarit
        const gabarit = toutes
            .filter((r: any) => !matchNiveau(r))
            .sort((a: any, b: any) => a.Priorite - b.Priorite)
        setLignes(gabarit.map((r: any) => ({
            CodeLangue: r.CodeLangue, Modele: r.Modele,
            RepliPoste: '', Priorite: r.Priorite, IsActif: r.IsActif !== false,
        })))
    }

    function ajouterLigne() {
        const dispo = props.languesActives.filter((l: any) => !lignes.some((x: any) => x.CodeLangue === l.CodeLangue))
        if (!dispo.length) { alert.warning('Toutes les langues actives sont déjà configurées.'); return }
        setLignes([...lignes, {
            CodeLangue: dispo[0].CodeLangue,
            Modele: `…{ticket}…{poste}…`, RepliPoste: '',
            Priorite: lignes.length + 1, IsActif: true,
        }])
    }

    function monter(i: number, d: -1 | 1) {
        const j = i + d
        if (j < 0 || j >= lignes.length) return
        const next = [...lignes]
            ;[next[i], next[j]] = [next[j], next[i]]
        next.forEach((l, k) => { l.Priorite = k + 1 })
        setLignes(next)
    }

    function enregistrer() {
        const actives = lignes.filter((l: any) => l.IsActif)
        if (!lignes.length || !actives.length) { alert.warning('Au moins une langue active requise.'); return }
        if (actives.length > 3) { alert.warning('3 langues actives au maximum par annonce (durée de lecture).'); return }
        for (const l of lignes) {
            if (!l.Modele?.trim()) { alert.warning(`Modèle vide : ${l.CodeLangue}`); return }
            if (!l.Modele.includes('{ticket}')) { alert.warning(`Le modèle doit contenir le repère {ticket} : ${l.CodeLangue}`); return }
            if (l.Modele.includes('{service}')) { alert.warning(`Repère {service} non pris en charge — utilisez {poste} : ${l.CodeLangue}`); return }
        }
        setSaving(true)
        postAdmin(props.authHeader, {
            Option: 'ENREGISTRER', ...scope(),
            Lignes: lignes.map((l: any, i: number) => ({
                CodeLangue: l.CodeLangue, Modele: l.Modele,
                Priorite: i + 1, IsActif: !!l.IsActif,
                ...(l.RepliPoste ? { RepliPoste: l.RepliPoste } : {}),
            })),
        })
            .then((res: any) => {
                if (res.StatutCode == 200) { alert.success(res.Message); loadList() }
                else alert.warning(res.Message)
            })
            .catch((e) => alert.error('Error', e))
            .finally(() => setSaving(false))
    }

    function revenirDefaut() {
        setSaving(true)
        postAdmin(props.authHeader, { Option: 'SUPPRIMER', ...scope() })
            .then((res: any) => {
                if (res.StatutCode == 200) { alert.success(res.Message); loadList() }
                else alert.warning(res.Message)
            })
            .catch((e) => alert.error('Error', e))
            .finally(() => setSaving(false))
    }

    function ecouter(l: any) {
        const texte = (l.Modele || '')
            .replaceAll('{ticket}', 'A001')
            .replaceAll('{poste}', (l.RepliPoste || '').replaceAll('{guichet}', '2') || 'au guichet 2')
            .replaceAll('{guichet}', '2')
        const prefixe = String(l.CodeLangue || '').slice(0, 2).toLowerCase()
        const v = (window.speechSynthesis?.getVoices() || [])
            .find((x) => x.lang?.toLowerCase().startsWith(prefixe))
        if (!v) { alert.warning(`Aucune voix ${l.CodeLangue} installée sur ce poste.`); return }
        const u = new SpeechSynthesisUtterance(texte)
        u.voice = v; u.lang = l.CodeLangue
        window.speechSynthesis.speak(u)
    }

    function chargerApercu() {
        if (!codeS) return
        setLoading(true)
        postAdmin(props.authHeader, { Option: 'APERCU', CodeS: codeS })
            .then((res: any) => {
                if (res.StatutCode == 200) setApercu(res.Result || [])
                else alert.warning(res.Message)
            })
            .catch((e) => alert.error('Error', e))
            .finally(() => setLoading(false))
    }

    const niveauOptions = [
        ...(props.isAdmin ? [{ value: 'entite', label: 'Entité (défaut global)' }] : []),
        { value: 'agence', label: 'Agence' },
        { value: 'service', label: 'Service' },
    ]

    return (
        <SettingCard title="Textes des annonces" icon={<ListMusic size={18} />}>
            <Spin spinning={loading}>
                {/* Sélecteur de niveau */}
                <div className="flex flex-wrap items-end gap-3 mb-4">
                    <div className="min-w-[200px]">
                        <Select label="Niveau" value={niveau} onChange={(v) => setNiveau(v as Niveau)} options={niveauOptions} />
                    </div>
                    {niveau !== 'entite' && (
                        <div className="min-w-[220px]">
                            <Select label="Agence" value={codeFiliale} onChange={(v) => { setCodeFiliale(v); setCodeS('') }}
                                options={props.filiales.map((f: any) => ({ value: f.CodeFiliale, label: `${f.CodeFiliale} - ${f.Libelle || ''}` }))} />
                        </div>
                    )}
                    {niveau === 'service' && (
                        <div className="min-w-[220px]">
                            <Select label="Service" value={codeS} onChange={setCodeS}
                                options={servicesAgence.map((s: any) => ({ value: s.CodeS, label: `${s.CodeS} - ${s.Libelle || ''}` }))} />
                        </div>
                    )}
                    {niveau === 'service' && codeS && (
                        <Button variant="outline" size="sm" startIcon={<Ear size={15} />} onClick={chargerApercu}>
                            Aperçu lu
                        </Button>
                    )}
                </div>

                <div className="mb-4 px-3 py-2 rounded-lg bg-[var(--eb-primary-soft)]/40 border border-[var(--eb-border)] text-sm">
                    Niveau réellement appliqué : <b>{niveauEffectif}</b>
                    {lignes.length === 0 && (niveau !== 'entite') && (
                        <Button variant="secondary" size="sm" className="ml-3" onClick={personnaliser}>
                            Personnaliser pour ce niveau
                        </Button>
                    )}
                </div>

                {/* Éditeur de lignes */}
                <div className="overflow-x-auto rounded-lg border border-[var(--eb-border)]">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-[var(--eb-bg)] text-left text-xs text-[var(--eb-text-muted)]">
                                <th className="px-3 py-2 font-semibold w-20">Priorité</th>
                                <th className="px-3 py-2 font-semibold w-36">Langue</th>
                                <th className="px-3 py-2 font-semibold">Texte</th>
                                <th className="px-3 py-2 font-semibold w-48">Phrase de repli (facult.)</th>
                                <th className="px-3 py-2 font-semibold w-16">Actif</th>
                                <th className="px-3 py-2 font-semibold w-24">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lignes.length === 0 && (
                                <tr><td colSpan={6} className="px-3 py-6 text-center text-xs text-[var(--eb-text-muted)]">
                                    {(niveau === 'agence' && !codeFiliale) || (niveau === 'service' && !codeS)
                                        ? 'Choisissez une cible pour voir ses textes'
                                        : 'Aucun texte à ce niveau — le niveau supérieur s’applique'}
                                </td></tr>
                            )}
                            {lignes.map((l: any, i: number) => (
                                <tr key={i} className="border-t border-[var(--eb-border)] align-top">
                                    <td className="px-3 py-2">
                                        <div className="flex items-center gap-1">
                                            <span className="font-mono font-bold w-5 text-center">{i + 1}</span>
                                            <IconButton size={22} color="default" onClick={() => monter(i, -1)}><ArrowUp size={13} /></IconButton>
                                            <IconButton size={22} color="default" onClick={() => monter(i, 1)}><ArrowDown size={13} /></IconButton>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <Select value={l.CodeLangue} options={props.languesActives.map((lg: any) => ({ value: lg.CodeLangue, label: lg.Libelle }))}
                                            onChange={(v) => { const n = [...lignes]; n[i].CodeLangue = v; setLignes(n) }} searchable={false} />
                                    </td>
                                    <td className="px-3 py-2 min-w-[280px]">
                                        <Textarea rows={2} value={l.Modele} disabled={!props.canEdit}
                                            onChange={(e) => { const n = [...lignes]; n[i].Modele = e.target.value; setLignes(n) }} />
                                    </td>
                                    <td className="px-3 py-2">
                                        <Input value={l.RepliPoste || ''} disabled={!props.canEdit} placeholder="ex. au guichet {guichet}"
                                            onChange={(e) => { const n = [...lignes]; n[i].RepliPoste = e.target.value; setLignes(n) }} />
                                    </td>
                                    <td className="px-3 py-2">
                                        <Switch checked={!!l.IsActif} onChange={(v) => { const n = [...lignes]; n[i].IsActif = v; setLignes(n) }} />
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex items-center gap-1">
                                            <IconButton tooltip="Écouter" color="primary" size={26} onClick={() => ecouter(l)}>
                                                <Ear size={14} />
                                            </IconButton>
                                            {props.canEdit && (
                                                <IconButton tooltip="Retirer" color="danger" size={26}
                                                    onClick={() => setLignes(lignes.filter((_, k) => k !== i).map((x, k) => ({ ...x, Priorite: k + 1 })))}>
                                                    <Trash2 size={14} />
                                                </IconButton>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {props.canEdit && (
                    <div className="flex flex-wrap items-center gap-2 mt-4">
                        <Button variant="outline" size="sm" startIcon={<Plus size={15} />} onClick={ajouterLigne}>
                            Ajouter une langue
                        </Button>
                        <div className="flex-1" />
                        <Button variant="outline" size="sm" loading={saving} onClick={revenirDefaut}
                            className="!text-red-600 !border-red-400/60 hover:!bg-red-50">
                            Revenir au niveau supérieur
                        </Button>
                        <Button variant="secondary" size="sm" loading={saving} startIcon={<Save size={16} />} onClick={enregistrer}>
                            Enregistrer
                        </Button>
                    </div>
                )}
            </Spin>

            {/* Aperçu lu (option APERCU — service uniquement) */}
            <FormDrawer open={!!apercu} onClose={() => setApercu(null)}
                title="Ce qui sera lu (exemple : ticket A001)" width={640}
                hint="Texte final par poste et par langue, avec le niveau de réglage réellement appliqué. « repli » = prononciation manquante, la phrase de repli est lue."
                footer={<Button variant="outline" onClick={() => setApercu(null)}>Fermer</Button>}>
                <div className="overflow-x-auto rounded-lg border border-[var(--eb-border)]">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-[var(--eb-bg)] text-left text-xs text-[var(--eb-text-muted)]">
                                <th className="px-3 py-2 font-semibold">Niveau</th>
                                <th className="px-3 py-2 font-semibold">Poste</th>
                                <th className="px-3 py-2 font-semibold">Langue</th>
                                <th className="px-3 py-2 font-semibold">Texte lu</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(apercu || []).map((a: any, i: number) => (
                                <tr key={i} className="border-t border-[var(--eb-border)]">
                                    <td className="px-3 py-2"><Badge color="blue">{a.Niveau}</Badge></td>
                                    <td className="px-3 py-2">{a.NumBox}{a.PrononciationManquante && <span className="text-amber-600 text-xs"> (repli)</span>}</td>
                                    <td className="px-3 py-2 font-mono text-xs">{a.Langue} #{a.Priorite}</td>
                                    <td className="px-3 py-2">{a.Texte}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </FormDrawer>
        </SettingCard>
    )
}

/* ================================================================== */
/*  Onglet Prononciation des postes                                    */
/* ================================================================== */
function TabPostes(props: {
    authHeader: () => string; user: User; canEdit: boolean;
    languesActives: any[]; filiales: any[]; services: any[];
}) {
    const alert = useAlert()
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [codeFiliale, setCodeFiliale] = useState('')
    const [codeS, setCodeS] = useState('')
    const [postes, setPostes] = useState<any[]>([])       // lignes brutes POSTES
    const [edit, setEdit] = useState<any>(null)           // {CodeS, NumBox, lignes:{CodeLangue,Prononciation}}

    const servicesAgence = props.services.filter((s: any) => !codeFiliale || s.CodeFiliale == codeFiliale)

    useEffect(() => {
        if (!codeFiliale) setCodeFiliale(props.user.CodeFiliale || props.filiales[0]?.CodeFiliale || '')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.filiales])

    useEffect(() => { loadPostes() /* eslint-disable-line react-hooks/exhaustive-deps */ }, [codeFiliale, codeS])

    function loadPostes() {
        if (!codeFiliale && !codeS) { setPostes([]); return }
        setLoading(true)
        postAdmin(props.authHeader, codeS ? { Option: 'POSTES', CodeS: codeS } : { Option: 'POSTES', CodeFiliale: codeFiliale })
            .then((res: any) => {
                if (res.StatutCode == 200) setPostes(res.Result || [])
                else alert.warning(res.Message)
            })
            .catch((e) => alert.error('Error', e))
            .finally(() => setLoading(false))
    }

    /** Regroupe les lignes par (CodeS, NumBox) → une ligne par poste. */
    const groupes = useMemo(() => {
        const m = new Map<string, any>()
        postes.forEach((r: any) => {
            const k = `${r.CodeS}|${r.NumBox}`
            if (!m.has(k)) m.set(k, {
                CodeS: r.CodeS, LibelleService: r.LibelleService,
                NumBox: r.NumBox, NbAgents: r.NbAgents, langues: {} as Record<string, any>,
            })
            m.get(k).langues[r.CodeLangue] = r
        })
        const arr: any[] = []
        m.forEach((v) => arr.push(v))
        return arr
    }, [postes])

    // Langues à afficher en colonnes : celles lues pour les services présents
    const languesColonnes = useMemo(() => {
        const codes: string[] = []
        postes.forEach((r: any) => { if (r.CodeLangue && !codes.includes(r.CodeLangue)) codes.push(r.CodeLangue) })
        return codes.map((c) => ({ code: c, libelle: postes.find((r: any) => r.CodeLangue === c)?.LibelleLangue || c }))
    }, [postes])

    function ouvrirEdition(g: any) {
        setEdit({
            CodeS: g.CodeS, NumBox: g.NumBox,
            lignes: languesColonnes.map((c) => ({
                CodeLangue: c.code,
                Prononciation: g.langues[c.code]?.Prononciation || '',
            })),
        })
    }

    function enregistrer() {
        const pronos = edit.lignes.filter((l: any) => l.Prononciation?.trim())
        if (!pronos.length) { alert.warning('Renseignez au moins une prononciation (ou utilisez Effacer).'); return }
        setSaving(true)
        postAdmin(props.authHeader, {
            Option: 'POSTES_ENREGISTRER', CodeS: edit.CodeS, NumBox: edit.NumBox,
            Prononciations: pronos.map((l: any) => ({ CodeLangue: l.CodeLangue, Prononciation: l.Prononciation })),
        })
            .then((res: any) => {
                if (res.StatutCode == 200) { alert.success(res.Message); setEdit(null); loadPostes() }
                else alert.warning(res.Message)
            })
            .catch((e) => alert.error('Error', e))
            .finally(() => setSaving(false))
    }

    function effacer(g: any) {
        setSaving(true)
        postAdmin(props.authHeader, { Option: 'POSTES_SUPPRIMER', CodeS: g.CodeS, NumBox: g.NumBox })
            .then((res: any) => {
                if (res.StatutCode == 200) { alert.success(res.Message); loadPostes() }
                else alert.warning(res.Message)
            })
            .catch((e) => alert.error('Error', e))
            .finally(() => setSaving(false))
    }

    return (
        <SettingCard title="Prononciation des postes" icon={<Mic2 size={18} />}>
            <Spin spinning={loading}>
                <div className="flex flex-wrap items-end gap-3 mb-4">
                    <div className="min-w-[220px]">
                        <Select label="Agence" value={codeFiliale} onChange={(v) => { setCodeFiliale(v); setCodeS('') }}
                            options={props.filiales.map((f: any) => ({ value: f.CodeFiliale, label: `${f.CodeFiliale} - ${f.Libelle || ''}` }))} />
                    </div>
                    <div className="min-w-[220px]">
                        <Select label="Service (facultatif)" value={codeS} onChange={setCodeS}
                            options={[{ value: '', label: 'Tous les services' },
                            ...servicesAgence.map((s: any) => ({ value: s.CodeS, label: `${s.CodeS} - ${s.Libelle || ''}` }))]} />
                    </div>
                    {props.canEdit && (
                        <Button variant="secondary" size="sm" startIcon={<Plus size={15} />}
                            onClick={() => setEdit({
                                CodeS: '', NumBox: '',
                                lignes: props.languesActives.map((l: any) => ({ CodeLangue: l.CodeLangue, Prononciation: '' })),
                                nouveau: true,
                            })}>
                            Ajouter un poste
                        </Button>
                    )}
                </div>

                <div className="overflow-x-auto rounded-lg border border-[var(--eb-border)]">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-[var(--eb-bg)] text-left text-xs text-[var(--eb-text-muted)]">
                                <th className="px-3 py-2 font-semibold">Service</th>
                                <th className="px-3 py-2 font-semibold w-20">Poste</th>
                                <th className="px-3 py-2 font-semibold w-20">Agents</th>
                                {languesColonnes.map((c) => (
                                    <th key={c.code} className="px-3 py-2 font-semibold">{c.libelle}</th>
                                ))}
                                {props.canEdit && <th className="px-3 py-2 font-semibold w-24">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {groupes.length === 0 && (
                                <tr><td colSpan={4 + languesColonnes.length} className="px-3 py-6 text-center text-xs text-[var(--eb-text-muted)]">
                                    Aucun poste — choisissez une agence
                                </td></tr>
                            )}
                            {groupes.map((g: any) => (
                                <tr key={`${g.CodeS}|${g.NumBox}`} className="border-t border-[var(--eb-border)]">
                                    <td className="px-3 py-2">
                                        <span className="font-semibold">{g.CodeS}</span>
                                        <span className="block text-xs text-[var(--eb-text-muted)]">{g.LibelleService}</span>
                                    </td>
                                    <td className="px-3 py-2 font-mono font-bold">{g.NumBox}</td>
                                    <td className="px-3 py-2">{g.NbAgents ?? 0}</td>
                                    {languesColonnes.map((c) => {
                                        const l = g.langues[c.code]
                                        const manquante = !l || l.Manquante || !l.Prononciation
                                        return (
                                            <td key={c.code} className={`px-3 py-2 text-sm ${manquante ? 'text-amber-600' : ''}`}>
                                                {manquante ? 'Manquante' : l.Prononciation}
                                            </td>
                                        )
                                    })}
                                    {props.canEdit && (
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-1">
                                                <IconButton tooltip="Modifier" color="primary" size={26} onClick={() => ouvrirEdition(g)}>
                                                    <Pencil size={14} />
                                                </IconButton>
                                                <IconButton tooltip="Effacer" color="danger" size={26} onClick={() => effacer(g)}>
                                                    <Trash2 size={14} />
                                                </IconButton>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Spin>

            {/* Édition / ajout d'un poste */}
            <FormDrawer open={!!edit} onClose={() => setEdit(null)}
                title={edit?.nouveau ? 'Ajouter un poste' : `Prononciation — ${edit?.CodeS} · poste ${edit?.NumBox}`}
                hint="Article compris : « à la caisse 1 du rez-de-chaussée ». Seul repère permis : {guichet}."
                footer={
                    <>
                        <Button variant="outline" onClick={() => setEdit(null)}>Annuler</Button>
                        <Button variant="secondary" loading={saving} startIcon={<Save size={16} />} onClick={enregistrer}>Enregistrer</Button>
                    </>
                }>
                {edit && (
                    <>
                        {edit.nouveau && (
                            <FormSection title="Poste">
                                <Select label="Service *" value={edit.CodeS} onChange={(v) => setEdit({ ...edit, CodeS: v })}
                                    options={props.services.map((s: any) => ({ value: s.CodeS, label: `${s.CodeS} - ${s.Libelle || ''}` }))} />
                                <Input label="Numéro de box *" value={edit.NumBox} placeholder="ex. 2"
                                    onChange={(e) => setEdit({ ...edit, NumBox: e.target.value })} />
                            </FormSection>
                        )}
                        <FormSection title="Prononciation par langue" subtitle="Laissez vide pour la phrase de repli (« au guichet {guichet} »).">
                            {edit.lignes.map((l: any, i: number) => (
                                <Input key={l.CodeLangue}
                                    label={`${props.languesActives.find((x: any) => x.CodeLangue === l.CodeLangue)?.Libelle || l.CodeLangue} (${l.CodeLangue})`}
                                    value={l.Prononciation}
                                    onChange={(e) => { const n = [...edit.lignes]; n[i].Prononciation = e.target.value; setEdit({ ...edit, lignes: n }) }} />
                            ))}
                        </FormSection>
                    </>
                )}
            </FormDrawer>
        </SettingCard>
    )
}
